import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, Loader2, X, Home, Briefcase } from "lucide-react";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { toast } from "sonner";

/**
 * Cached coarse location bias used by every AddressInput in this tab.
 * We probe once on first AddressInput mount (non-blocking) and reuse.
 * `null` = not probed yet · `{lat,lng}` = got it · `false` = denied / unavailable
 */
let LOCATION_BIAS_PROMISE = null;

function getLocationBiasOnce() {
  if (LOCATION_BIAS_PROMISE) return LOCATION_BIAS_PROMISE;
  LOCATION_BIAS_PROMISE = new Promise((resolve) => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator) || !window.isSecureContext) {
      resolve(false);
      return;
    }
    // Only ask the browser if we already have permission — don't prompt users
    // out of the blue just to bias autocomplete. A Permissions API check lets
    // us detect the granted state silently.
    const runGeo = () => {
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: +p.coords.latitude.toFixed(4), lng: +p.coords.longitude.toFixed(4) }),
        () => resolve(false),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 10 * 60 * 1000 }
      );
    };
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "geolocation" }).then((res) => {
        if (res.state === "granted") runGeo();
        else resolve(false);
      }).catch(() => resolve(false));
    } else {
      resolve(false);
    }
  });
  return LOCATION_BIAS_PROMISE;
}

/**
 * Address input with Google Places autocomplete (via backend proxy).
 *
 * Features:
 *  - Location-biased suggestions (local results first) when the user has
 *    already granted geolocation permission. Never prompts.
 *  - Dropdown sits directly beneath the input and is sized via `visualViewport`
 *    so it never hides behind the on-screen keyboard on iOS/Android.
 *  - Clear ✕ button appears inside the input when it has content.
 */
export default function AddressInput({
  value,
  onChange,
  placeholder = "Address",
  allowCurrentLocation = false,
  showSavedPlaces = true,
  testId,
  onOpenChange,
  autoFocus = false,
  variant = "default", // "default" (light surface) | "dark" (dark glass surface)
  inlineSuggestions = false, // when true, suggestions render as an inline dark-glass panel
                             // directly beneath the input INSIDE the parent card
                             // (no portal, no fixed positioning), capped at
                             // ~3 rows with internal scroll. Used by the
                             // Address Entry rebuild in BookingSheet.
}) {
  const { user } = useAuth() || {};
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loadingGps, setLoadingGps] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [maxPanelH, setMaxPanelH] = useState(320); // px
  const [bias, setBias] = useState(null);
  // Fixed-position rect for the suggestions panel so it escapes any
  // parent `overflow: hidden` (e.g. the booking pill clips at its border).
  const [panelRect, setPanelRect] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const tRef = useRef(null);
  const ignoreNextFetch = useRef(false);
  const focused = useRef(false);
  const isDark = variant === "dark";

  // Notify parent so it can hide overlapping overlays (route bar, distance card)
  // while the suggestions panel is open. Only fires when something is visible.
  useEffect(() => {
    if (onOpenChange) onOpenChange(open && suggestions.length > 0);
  }, [open, suggestions.length, onOpenChange]);

  // Probe (silent) location bias once per tab
  useEffect(() => {
    let mounted = true;
    getLocationBiasOnce().then((b) => { if (mounted && b) setBias(b); });
    return () => { mounted = false; };
  }, []);

  // autoFocus: parent-driven focus (e.g. pickup-just-selected → focus destination)
  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => {
      try { inputRef.current?.focus(); } catch (_) {}
    }, 60);
    return () => clearTimeout(t);
  }, [autoFocus]);


  // Debounced autocomplete fetch — includes bias when available
  useEffect(() => {
    if (ignoreNextFetch.current) { ignoreNextFetch.current = false; return; }
    if (!value || value.length < 3) { setSuggestions([]); setOpen(false); return; }
    clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      try {
        const params = { q: value };
        if (bias?.lat != null && bias?.lng != null) {
          params.lat = bias.lat;
          params.lng = bias.lng;
        }
        const { data } = await api.get("/places/autocomplete", { params });
        const list = data.suggestions || [];
        setSuggestions(list);
        setOpen(list.length > 0 && focused.current);
        setHighlight(-1);
      } catch (err) {
        console.warn("Places autocomplete failed:", err?.message || err);
        setSuggestions([]);
        setOpen(false);
      }
    }, 250);
    return () => clearTimeout(tRef.current);
  }, [value, bias]);

  // Close on outside click (desktop mouse) + on touch outside the box.
  // The suggestions panel lives in a portal (document.body) so we have to
  // explicitly ignore taps on it, otherwise the panel would close before
  // the suggestion's click event fires.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && boxRef.current.contains(e.target)) return;
      const panel = document.querySelector(`[data-testid="${testId}-suggestions"]`);
      if (panel && panel.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [testId]);

  // Keep the dropdown height constrained so it never hides behind the keyboard.
  // visualViewport is the part of the page the user can actually see on iOS/Android.
  // We ALSO measure the input's screen rect so the panel can render with
  // `position: fixed` — escaping any parent `overflow: hidden` (the booking
  // pill clips its content for the morph animation, so a pill-relative
  // dropdown would be cropped at the pill's border).
  //
  // SKIPPED entirely in `inlineSuggestions` mode — the suggestions render as
  // a normal inline block inside the parent card, so there's nothing to
  // portal or reposition. Card sizing + internal scroll is the card's job.
  useEffect(() => {
    if (inlineSuggestions) return undefined;
    const recalc = () => {
      if (!open || !boxRef.current) return;
      const rect = boxRef.current.getBoundingClientRect();
      const vv = window.visualViewport;
      const viewportTop = vv ? vv.offsetTop : 0;
      const viewportBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const spaceBelow = Math.floor(viewportBottom - rect.bottom - 12);
      const spaceAbove = Math.floor(rect.top - viewportTop - 12);
      // Aggressive flip-up: when both directions have room, prefer the
      // direction with more room. The bottom-anchored booking pill almost
      // always has more room above, so the dropdown reads naturally
      // upward instead of getting clipped at the safe-area edge.
      const minPanel = 180;
      const flipUp = spaceAbove > spaceBelow && spaceAbove >= minPanel;
      const available = flipUp ? spaceAbove : Math.max(140, spaceBelow);
      // Allow taller dropdowns so the rider can see more suggestions
      // without scrolling — matches the Uber/Lyft "full-list" UX.
      const h = Math.min(available, 460);
      setMaxPanelH(h);
      // Widen the panel beyond the bare input — riders often type long
      // addresses and the suggestions wrap awkwardly when constrained to
      // the input's exact width. Cap at 560px / viewport-24 and center.
      const targetWidth = Math.min(560, Math.max(rect.width, window.innerWidth - 24));
      const widerLeft = Math.max(12, Math.min(rect.left, window.innerWidth - targetWidth - 12));
      setPanelRect({
        left: widerLeft,
        width: targetWidth,
        top: flipUp ? rect.top - 8 - h : rect.bottom + 8,
        flipUp,
      });
    };
    recalc();
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", recalc);
      vv.addEventListener("scroll", recalc);
    }
    window.addEventListener("resize", recalc);
    window.addEventListener("scroll", recalc, true);
    return () => {
      if (vv) {
        vv.removeEventListener("resize", recalc);
        vv.removeEventListener("scroll", recalc);
      }
      window.removeEventListener("resize", recalc);
      window.removeEventListener("scroll", recalc, true);
    };
  }, [open, suggestions.length, inlineSuggestions]);

  const pick = (s) => {
    ignoreNextFetch.current = true;
    onChange(s.text);
    setSuggestions([]);
    setOpen(false);
    try { inputRef.current?.blur(); } catch (_) {}
  };

  const onFocus = () => {
    focused.current = true;
    if (suggestions.length) setOpen(true);
    else if (inlineSuggestions && savedPlaces.length > 0 && !value) setOpen(true);
    // Intentionally NOT calling scrollIntoView here — the focused input
    // should stay where it is on the page. The keyboard shrinks the
    // visual viewport via `interactive-widget=resizes-content` (set in
    // index.html) so the input + a few suggestions remain in view
    // without jarring the rest of the layout. Calling scrollIntoView
    // was previously causing the body itself to scroll, which broke
    // the "fixed viewport" feel the booking surfaces are designed for.
  };

  const useGps = () => {
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    if (!window.isSecureContext) {
      toast.error("Location requires HTTPS. Try reloading the page.");
      return;
    }
    setLoadingGps(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // Also remember this position for autocomplete bias on this tab
        const b = { lat: +pos.coords.latitude.toFixed(4), lng: +pos.coords.longitude.toFixed(4) };
        setBias(b);
        LOCATION_BIAS_PROMISE = Promise.resolve(b);
        try {
          const { data } = await api.get("/places/reverse", {
            params: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          });
          if (data.address) {
            ignoreNextFetch.current = true;
            onChange(data.address);
            toast.success("Location set");
          } else {
            toast.error("Couldn't read your address. Try typing it instead.");
          }
        } catch (e) {
          console.error("Reverse geocode failed", {
            message: e?.message,
            status: e?.response?.status,
            detail: e?.response?.data?.detail,
            url: e?.config?.url,
          });
          const apiDetail = e?.response?.data?.detail;
          if (apiDetail) {
            toast.error(formatApiError(apiDetail));
          } else if (e?.response?.status) {
            toast.error(`Reverse geocoding failed (${e.response.status}). Try typing the address instead.`);
          } else if (!navigator.onLine) {
            toast.error("You're offline. Check your connection and try again.");
          } else {
            toast.error("Could not reach the server. Please try typing the address.");
          }
        } finally {
          setLoadingGps(false);
        }
      },
      (err) => {
        setLoadingGps(false);
        if (err.code === 1) toast.error("Location permission denied. Enable Location in your browser settings.");
        else if (err.code === 2) toast.error("Location unavailable — try again somewhere with better signal.");
        else if (err.code === 3) toast.error("Location request timed out. Try again.");
        else toast.error("Couldn't get your location.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const onKeyDown = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") setOpen(false);
  };

  // Saved places — Home from profile.home_address, Work from address_book (label ~ "work")
  const savedPlaces = useMemo(() => {
    if (!showSavedPlaces || !user) return [];
    const out = [];
    if (user.home_address) out.push({ key: "home", label: "Home", address: user.home_address, Icon: Home });
    const book = Array.isArray(user.address_book) ? user.address_book : [];
    const work = book.find((a) => typeof a?.label === "string" && a.label.trim().toLowerCase() === "work");
    if (work?.address) out.push({ key: "work", label: "Work", address: work.address, Icon: Briefcase });
    // also surface any other labelled entries (capped to 2) so saved-places pattern is useful beyond home/work
    for (const a of book) {
      if (!a?.address || !a?.label) continue;
      const lbl = a.label.trim();
      if (lbl.toLowerCase() === "work") continue;
      if (out.length >= 4) break;
      out.push({ key: `ab-${a.id || lbl}`, label: lbl, address: a.address, Icon: MapPin });
    }
    return out;
  }, [user, showSavedPlaces]);

  const pickSaved = (addr) => {
    ignoreNextFetch.current = true;
    onChange(addr);
    setSuggestions([]);
    setOpen(false);
    try { inputRef.current?.blur(); } catch (_) {}
  };

  // Tell the parent the dropdown is also "open" when the empty-state saved-places
  // chips are visible — so overlays like the route breadcrumb don't bleed through.
  useEffect(() => {
    if (!onOpenChange) return;
    const showingSaved = open && savedPlaces.length > 0 && !value && suggestions.length === 0;
    if (showingSaved) onOpenChange(true);
  }, [open, savedPlaces.length, value, suggestions.length, onOpenChange]);

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input
          ref={inputRef}
          className={`input-line w-full ${isDark ? "input-line-dark" : ""} ${value ? "pr-8" : (allowCurrentLocation ? "pr-9" : "")}`}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={() => { focused.current = false; }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          data-testid={testId}
        />
        {value ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange("");
              setSuggestions([]);
              setOpen(false);
              try { inputRef.current?.focus(); } catch (_) {}
            }}
            aria-label="Clear"
            className={`absolute right-1 top-1/2 -translate-y-1/2 p-1.5 ${isDark ? "text-[#F9F7F4]/55 hover:text-[#ed6500]" : "text-[#515E7B] hover:text-[#ed6500]"} active:text-[#ed6500]`}
            data-testid={`${testId}-clear`}
          >
            <X size={14} />
          </button>
        ) : (
          allowCurrentLocation && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={useGps}
              disabled={loadingGps}
              aria-label="Use my current location"
              title="Use my current location"
              className={`absolute right-1 top-1/2 -translate-y-1/2 p-1.5 ${isDark ? "text-[#5c97f7] hover:text-[#ed6500]" : "text-[#0074d4] hover:text-[#ed6500]"} active:text-[#ed6500] disabled:opacity-60`}
              data-testid={`${testId}-gps`}
            >
              {loadingGps ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
            </button>
          )
        )}
      </div>

      {open && inlineSuggestions && (suggestions.length > 0 || (savedPlaces.length > 0 && !value)) && (
        <div
          className="mt-1.5 rounded-2xl overflow-hidden"
          style={{
            // Dark glass anchored inline directly beneath the field,
            // matching the entry.html spec. ~3 visible rows @ 52px
            // each; internal scroll takes over beyond that so the
            // panel never pushes past the mobile keyboard.
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.03)",
            maxHeight: 156,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
          data-testid={`${testId}-suggestions`}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {savedPlaces.length > 0 && !value && (
            <div
              className="flex flex-wrap gap-1.5 px-3 py-2"
              style={{ borderBottom: suggestions.length ? "1px solid rgba(255,255,255,0.06)" : "none" }}
              data-testid={`${testId}-saved-places`}
            >
              {savedPlaces.map(({ key, label, address, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSaved(address)}
                  title={address}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-widest uppercase transition-colors"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    color: "#F9F7F4",
                  }}
                  data-testid={`${testId}-saved-${key}`}
                >
                  <Icon size={11} strokeWidth={1.8} className="text-[#ed6500]" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
          {suggestions.length > 0 && (
            <div>
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id || i}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  className="w-full text-left px-3.5 py-3 transition-colors flex items-start gap-3"
                  style={{
                    background: i === highlight ? "rgba(255,255,255,0.08)" : "transparent",
                    borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
                  }}
                  data-testid={`${testId}-suggestion-${i}`}
                >
                  <MapPin size={14} className="text-[#ed6500] mt-0.5 shrink-0" />
                  {s.primary ? (
                    <span className="leading-snug min-w-0 flex-1">
                      <span
                        className="block font-semibold text-[13px] text-[#F9F7F4] truncate"
                        data-testid={`${testId}-suggestion-${i}-primary`}
                      >
                        {s.primary}
                      </span>
                      {s.secondary && (
                        <span
                          className="block text-[11.5px] text-[#F9F7F4]/60 truncate mt-0.5"
                          data-testid={`${testId}-suggestion-${i}-secondary`}
                        >
                          {s.secondary}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[13px] text-[#F9F7F4] leading-snug truncate flex-1">{s.text}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {open && !inlineSuggestions && panelRect && (suggestions.length > 0 || (savedPlaces.length > 0 && !value)) && createPortal(
        <div
          className="fixed z-[100] bg-white border hairline shadow-2xl overflow-y-auto rounded-2xl"
          style={{
            left: panelRect.left,
            top: panelRect.top,
            width: panelRect.width,
            maxHeight: `${maxPanelH}px`,
          }}
          data-testid={`${testId}-suggestions`}
          // Block the document-level outside-click handler from firing when
          // the user taps inside the portal (the portal lives outside boxRef
          // so the standard `boxRef.contains(target)` check would close the
          // panel before the click registers).
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* Saved-places chips render at the top of the dropdown when the input
              is empty — keeps the address bar slim while still surfacing
              one-tap shortcuts for Home / Work. */}
          {savedPlaces.length > 0 && !value && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b hairline bg-[#F9F7F4]" data-testid={`${testId}-saved-places`}>
              {savedPlaces.map(({ key, label, address, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickSaved(address)}
                  title={address}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 border hairline bg-white hover:bg-white text-[11px] tracking-widest uppercase text-[#515E7B] hover:text-[#ed6500] transition-colors"
                  data-testid={`${testId}-saved-${key}`}
                >
                  <Icon size={11} strokeWidth={1.8} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          )}
          {suggestions.length > 0 && (
            <div className="divide-y divide-[#E5E1D8]">
              {suggestions.map((s, i) => (
                <button
                  key={s.place_id || i}
                  type="button"
                  // prevent input blur so tap registers before the keyboard dismisses
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={() => pick(s)}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    i === highlight ? "bg-[#F9F7F4]" : "bg-white active:bg-[#F9F7F4] hover:bg-[#F9F7F4]"
                  }`}
                  data-testid={`${testId}-suggestion-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <MapPin size={14} className="text-[#ed6500] mt-0.5 shrink-0" />
                    {s.primary ? (
                      // Two-line layout: primary (business / street name in
                      // semibold navy) + secondary (city/state in muted
                      // slate). Falls through to the flat `text` when the
                      // backend didn't surface a structured split.
                      <span className="leading-snug min-w-0 flex-1">
                        <span
                          className="block font-semibold text-[#0A1128] truncate"
                          data-testid={`${testId}-suggestion-${i}-primary`}
                        >
                          {s.primary}
                        </span>
                        {s.secondary && (
                          <span
                            className="block text-[12px] text-[#515E7B] truncate mt-0.5"
                            data-testid={`${testId}-suggestion-${i}-secondary`}
                          >
                            {s.secondary}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="leading-snug">{s.text}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
